import { getBusinessList, getLoadPending, getOpenBusinesses } from 'app-components/flux/business/businessSelectors';
import { rootUiActions } from 'app-components/flux/root-ui';
import { useFetchOnce } from 'app-components/hooks/utils';
import { getLogger } from 'app-components/utils/core';
import { INVOICE_FORM_ROOTUI_TYPE } from 'containers/InvoiceForm/actions';
import type { InvoiceFormModalProps } from 'containers/InvoiceForm/types';
import { BusinessUpgradeUpsertAction } from 'containers/UpgradeToBusiness/actions';
import { useEffect } from 'react';

import { businessActions, businessSelectors } from 'app-components/flux/business';
import { getTransactionById } from 'app-components/flux/transactions/transactionsSelectors';
import { TransactionSubType } from 'app-components/flux/transactions/transactionsTypes';
import { createGuestCustomerIfNoneExists } from 'app-components/flux/customers/customersThunks';
import { tracker } from 'app-components/utils/core';
import { useAppDispatch, useAppSelector, useAppStore } from 'hooks/redux';
import { useUrlDialog } from 'hooks/useUrlDialog';
import { useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import { getFeatureFlag } from 'app-components/flux/feature-flags/featureFlagsSelectors';

const log = getLogger('invoices/index.tsx');

const getTitleBySubtype = (subtype: TransactionSubType | undefined): string => {
  switch (subtype) {
    case TransactionSubType.SalesReceipt:
      return 'Sales Receipt';
    case TransactionSubType.InvoiceCreditMemo:
      return 'Credit Memo';
    case TransactionSubType.Estimate:
      return 'Estimate';
    case TransactionSubType.Invoice:
    default:
      return 'Invoice';
  }
};

export const useOpenBusinessUpgrade = () => {
  useFetchOnce(businessActions.getBusiness, businessSelectors, false);
  const dispatch = useAppDispatch();

  const noBusinesses = useAppSelector((state) => !getLoadPending(state) && getBusinessList(state).size === 0);

  const triggerBusinessUpgrade = useCallback(() => {
    dispatch(
      BusinessUpgradeUpsertAction({
        onClose: () => dispatch(rootUiActions.removeDialog('upgrade-to-business')),
        location: 'default',
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (noBusinesses) {
      log.debug('showBusinessUpgradeFlow');
      triggerBusinessUpgrade();
    }
  }, [dispatch, noBusinesses, triggerBusinessUpgrade]);

  return { triggerBusinessUpgrade, noBusinesses };
};

export const useOpenInvoice = (context: string) => {
  useFetchOnce(businessActions.getBusiness, businessSelectors, false);
  const store = useAppStore();
  const dispatch = useAppDispatch();
  const { openDialog } = useUrlDialog<InvoiceFormModalProps>();

  const openInvoice = useCallback(
    (props: InvoiceFormModalProps) => {
      const dialogProps = { context, ...props };
      openDialog(INVOICE_FORM_ROOTUI_TYPE, dialogProps, {
        replace: false,
        rootUi: dialogProps.txnActionsCallback !== undefined,
      });
    },
    [context, openDialog],
  );

  const openUpgradeDialog = useCallback(() => {
    log.debug('showBusinessUpgradeFlow');
    dispatch(
      BusinessUpgradeUpsertAction({
        onClose: () => dispatch(rootUiActions.removeDialog('upgrade-to-business')),
        location: 'default',
      }),
    );
  }, [dispatch]);

  const openInvoiceIfAllowed = useCallback(
    async ({
      invoiceId,
      subtype: subtypeProp,
      originalTxnId,
      initialView: initialViewProp,
      txnActionsCallback,
      ...other
    }: InvoiceFormModalProps = {}) => {
      const noOpenBusinesses = getOpenBusinesses(store.getState()).size === 0;
      const noBusinesses = getBusinessList(store.getState()).size === 0;
      const txn = invoiceId ? getTransactionById(store.getState(), invoiceId) : undefined;
      const guestClientFf = getFeatureFlag(store.getState(), 'qbpGuestClient');
      const subtype = txn?.subtype || subtypeProp || TransactionSubType.Invoice;
      const startingPoint = originalTxnId != null ? 'Transaction' : 'Sales Receipt';

      if (noBusinesses || (noOpenBusinesses && !invoiceId)) {
        openUpgradeDialog();
      } else {
        const clientId = uuid();
        const initialView =
          initialViewProp || (txn?.subtype === TransactionSubType.SalesReceipt || txn?.sentOn ? 'Preview' : 'Edit');
        if (!invoiceId) {
          tracker.track(tracker.events.createInvoiceStart, {
            traceId: clientId,
            subtype,
            ...(subtype === TransactionSubType.SalesReceipt && {
              starting_point: startingPoint,
            }),
          });
        }

        if (subtype === 'SALES_RECEIPT' && guestClientFf) {
          await dispatch(createGuestCustomerIfNoneExists()).unwrap();
        }

        openInvoice({
          invoiceId,
          clientId,
          subtype,
          initialView,
          titleProp: getTitleBySubtype(subtype),
          originalTxnId,
          startingPoint,
          txnActionsCallback,
          ...other,
        });
      }
    },
    [dispatch, openInvoice, openUpgradeDialog, store],
  );

  return openInvoiceIfAllowed;
};
